import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PssPlayerDetails, PssItemDesign, PssInventoryItem, PssCharacterDesign, PssRoomDesign, PssCrewMember } from '../../../services/pss-auth.service';
import { spriteIdOrEmpty, rarityRank } from '../pss-display-utils';
import { FeatureFlagService, FEATURE_FLAGS } from '../../../services/feature-flag.service';
import { itemAssignedStat, itemCrewStat, itemCrewStatInfo } from './inventory-utils';
import { PssSpriteComponent } from '../../pss-sprite/pss-sprite.component';

@Component({
  selector: 'app-inventory-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, PssSpriteComponent],
  templateUrl: './inventory-tab.component.html',
  styleUrls: ['./inventory-tab.component.css']
})
export class InventoryTabComponent {
  private static readonly STORAGE_KEY = 'pss_inventory_filters';

  @Input() details: PssPlayerDetails | null = null;
  @Input() itemDesigns = new Map<string, PssItemDesign>();
  @Input() characterDesigns = new Map<string, PssCharacterDesign>();
  @Input() roomDesigns = new Map<string, PssRoomDesign>();
  @Input() invNewItems = new Set<string>();

  @Output() useItemRequested = new EventEmitter<PssInventoryItem>();

  invSearch = '';
  invSubtypeFilter = 'all';
  invRarityFilter = 'all';
  invShowNewOnly = false;
  invSortBy: 'name' | 'qty' = 'name';
  invSortAsc = true;

  get basicPlus(): boolean { return this.featureFlags.isBasicPlus(); }
  get restrictedMode(): boolean { return this.featureFlags.isRestrictedMode(); }

  // Diagnostic render modes, toggled via URL query param `?inv=...`. Used to
  // bisect a mobile-only freeze-on-load with no dev tools available on device:
  //   ?inv=nosprites  → render rows without sprite images (isolates image/decode/memory cost)
  //   ?inv=plain      → name + qty only, no sprites or stats (isolates raw render volume)
  // Anything else (or absent) renders the full UI.
  renderMode: 'full' | 'nosprites' | 'plain' = 'full';
  showSprites = true;
  showStats = true;

  constructor(private featureFlags: FeatureFlagService) {
    this.loadFilters();
    this.applyRenderMode();
  }
  expandedDesignId: string | null = null;

  private applyRenderMode(): void {
    let mode = '';
    try { mode = new URLSearchParams(window.location.search).get('inv') ?? ''; } catch {}
    this.renderMode = mode === 'nosprites' || mode === 'plain' ? mode : 'full';
    this.showSprites = this.renderMode === 'full';
    this.showStats = this.renderMode !== 'plain';
  }

  // Memoization caches. The template binds these getters and they are invoked on
  // every change-detection cycle; without caching they re-filter/group/sort the
  // whole inventory and return a fresh array reference each tick, which makes
  // Angular tear down and rebuild the entire item list (and every child sprite)
  // on every cycle — enough to freeze the main thread for large inventories.
  private groupedCache: { subtype: string; label: string; items: PssInventoryItem[] }[] | null = null;
  private groupedKey: unknown[] | null = null;
  private filterOptsCache: { value: string; label: string }[] | null = null;
  private filterOptsKey: unknown[] | null = null;
  private rarityOptsCache: { value: string; label: string }[] | null = null;
  private rarityOptsKey: unknown[] | null = null;

  trackGroup(_i: number, g: { subtype: string }): string { return g.subtype; }
  trackItem(_i: number, item: PssInventoryItem): string { return item.designId; }

  private sameKey(a: unknown[] | null, b: unknown[]): boolean {
    if (!a || a.length !== b.length) return false;
    for (let i = 0; i < b.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }

  get groupedInventory(): { subtype: string; label: string; items: PssInventoryItem[] }[] {
    const key = [
      this.details?.inventory ?? null, this.itemDesigns, this.invNewItems.size,
      this.invSearch, this.invSubtypeFilter, this.invRarityFilter,
      this.invShowNewOnly, this.invSortBy, this.invSortAsc,
    ];
    if (this.groupedCache && this.sameKey(this.groupedKey, key)) return this.groupedCache;
    this.groupedKey = key;
    return this.groupedCache = this.computeGroupedInventory();
  }

  private computeGroupedInventory(): { subtype: string; label: string; items: PssInventoryItem[] }[] {
    const q = this.invSearch.toLowerCase();
    let list = (this.details?.inventory ?? []).filter(i => this.isDisplayableItem(i));
    if (this.invShowNewOnly) list = list.filter(i => this.invNewItems.has(i.designId));
    if (q) list = list.filter(i => this.itemName(i.designId).toLowerCase().includes(q));
    if (this.invSubtypeFilter !== 'all') {
      list = list.filter(i => (this.itemDesigns.get(i.designId)?.subtype ?? '') === this.invSubtypeFilter);
    }
    if (this.invRarityFilter !== 'all') {
      list = list.filter(i => this.itemRarity(i.designId).toLowerCase() === this.invRarityFilter);
    }

    const dir = this.invSortAsc ? 1 : -1;
    const groups = new Map<string, PssInventoryItem[]>();

    list.forEach(item => {
      const sub = this.itemDesigns.get(item.designId)?.subtype ?? '';
      if (!groups.has(sub)) groups.set(sub, []);
      groups.get(sub)!.push(item);
    });

    groups.forEach(items => items.sort((a, b) =>
      this.invSortBy === 'qty'
        ? dir * (b.quantity - a.quantity)
        : dir * this.itemName(a.designId).localeCompare(this.itemName(b.designId))
    ));

    return Array.from(groups.entries())
      .map(([subtype, items]) => ({ subtype, label: this.subtypeLabel(subtype), items }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  get invFilterOptions(): { value: string; label: string }[] {
    const key = [this.details?.inventory ?? null, this.itemDesigns];
    if (this.filterOptsCache && this.sameKey(this.filterOptsKey, key)) return this.filterOptsCache;
    this.filterOptsKey = key;
    return this.filterOptsCache = this.computeInvFilterOptions();
  }

  private computeInvFilterOptions(): { value: string; label: string }[] {
    const seen = new Set<string>();
    (this.details?.inventory ?? []).forEach(item => {
      if (!this.isDisplayableItem(item)) return;
      const sub = this.itemDesigns.get(item.designId)?.subtype ?? '';
      if (sub) seen.add(sub);
    });
    const opts = Array.from(seen).sort().map(s => ({ value: s, label: this.subtypeLabel(s) }));
    return [{ value: 'all', label: 'All Types' }, ...opts];
  }

  get invRarityOptions(): { value: string; label: string }[] {
    const key = [this.details?.inventory ?? null, this.itemDesigns];
    if (this.rarityOptsCache && this.sameKey(this.rarityOptsKey, key)) return this.rarityOptsCache;
    this.rarityOptsKey = key;
    return this.rarityOptsCache = this.computeInvRarityOptions();
  }

  private computeInvRarityOptions(): { value: string; label: string }[] {
    const seen = new Set<string>();
    (this.details?.inventory ?? []).forEach(item => {
      if (!this.isDisplayableItem(item)) return;
      const rarity = this.itemRarity(item.designId).trim();
      if (rarity) seen.add(rarity);
    });
    const opts = Array.from(seen)
      .sort((a, b) => {
        const ra = rarityRank(a);
        const rb = rarityRank(b);
        if (ra !== rb) return ra - rb;
        return a.localeCompare(b);
      })
      .map(r => ({ value: r.toLowerCase(), label: r }));
    return [{ value: 'all', label: 'All Rarities' }, ...opts];
  }

  setInvSort(col: 'name' | 'qty'): void {
    if (this.invSortBy === col) { this.invSortAsc = !this.invSortAsc; }
    else { this.invSortBy = col; this.invSortAsc = col !== 'qty'; }
    this.saveFilters();
  }

  get hasActiveFilters(): boolean {
    return !!this.invSearch
      || this.invSubtypeFilter !== 'all'
      || this.invRarityFilter !== 'all'
      || this.invShowNewOnly;
  }

  clearFilters(): void {
    this.invSearch = '';
    this.invSubtypeFilter = 'all';
    this.invRarityFilter = 'all';
    this.invShowNewOnly = false;
    this.saveFilters();
  }

  setInvSearch(q: string): void { this.invSearch = q; this.saveFilters(); }
  setInvSubtypeFilter(v: string): void { this.invSubtypeFilter = v; this.saveFilters(); }
  setInvRarityFilter(v: string): void { this.invRarityFilter = v; this.saveFilters(); }
  setInvNewOnly(v: boolean): void { this.invShowNewOnly = v; this.saveFilters(); }

  invIsOnShip(item: PssInventoryItem): boolean {
    return item.equippedBy.some(c => c.deployed && !c.donated);
  }

  invFreeCount(item: PssInventoryItem): number {
    return item.quantity - item.equippedBy.length;
  }

  itemName(designId: string): string {
    return this.itemDesigns.get(designId)?.name ?? `#${designId}`;
  }

  itemSpriteId(designId: string): string {
    return spriteIdOrEmpty(this.itemDesigns.get(designId)?.logoSpriteId);
  }

  itemRarity(designId: string): string {
    return this.itemDesigns.get(designId)?.rarity ?? '';
  }

  itemDescription(designId: string): string {
    return this.itemDesigns.get(designId)?.description?.trim() ?? '';
  }

  crewName(c: PssCrewMember): string {
    return c.customName || this.characterDesigns.get(c.designId)?.name || `#${c.designId}`;
  }

  crewSpriteId(designId: string): string {
    return spriteIdOrEmpty(this.characterDesigns.get(designId)?.profileSpriteId);
  }

  crewRoomName(c: PssCrewMember): string {
    if (c.donated) return 'Donated';
    if (!c.deployed || c.roomId === '0' || !c.roomDesignId) return 'Inventory';
    return this.roomDesigns.get(c.roomDesignId)?.name ?? `Room#${c.roomDesignId}`;
  }

  itemTypeLabel(designId: string): string {
    const d = this.itemDesigns.get(designId);
    if (!d) return 'Unknown';
    const sub = this.subtypeLabel(d.subtype ?? '');
    if (sub && sub !== d.subtype) return sub;
    return this.humanizeWords(d.itemType || d.subtype || 'Unknown');
  }

  itemBonusRows(item: PssInventoryItem): Array<{ label: string; value: string }> {
    return this.bonusRows(item);
  }

  itemMainBonusText(item: PssInventoryItem): string {
    const bonuses = this.itemBonusRows(item);
    if (!bonuses.length) return '';
    return bonuses[0].value;
  }

  itemAdditionalBonusText(item: PssInventoryItem): string {
    const bonuses = this.itemBonusRows(item);
    if (bonuses.length <= 1) return '';
    return bonuses.slice(1).map(b => b.value).join(', ');
  }

  rarityColor(rarityValue: string): string {
    if (!rarityValue || rarityValue === 'all') return '';
    return `var(--rarity-${rarityValue.toLowerCase()})`;
  }

  toggleExpanded(item: PssInventoryItem): void {
    this.expandedDesignId = this.expandedDesignId === item.designId ? null : item.designId;
  }

  isExpanded(item: PssInventoryItem): boolean {
    return this.expandedDesignId === item.designId;
  }

  requestUseItem(item: PssInventoryItem, event: Event): void {
    event.stopPropagation();
    if (this.restrictedMode) return;
    this.useItemRequested.emit(item);
  }

  isConsumable(item: PssInventoryItem): boolean {
    const d = this.itemDesigns.get(item.designId);
    if (!d) return false;
    const consumable = (d.flags & 64) !== 0;
    const useOnCrew = (d.flags & 1024) !== 0;
    return consumable && !useOnCrew && item.freeInstances.length > 0;
  }

  itemAssignedStat(item: PssInventoryItem): string {
    return itemAssignedStat(item, this.itemDesigns);
  }

  itemCrewStat(item: PssInventoryItem, crew: PssCrewMember): string {
    return itemCrewStat(item, this.itemDesigns, crew);
  }

  itemCrewStatMatch(item: PssInventoryItem, crew: PssCrewMember): boolean {
    return itemCrewStatInfo(item, this.itemDesigns, crew).matchMain;
  }


  private parseItemBonusValue(raw?: string): number {
    if (!raw) return 0;
    const match = raw.trim().match(/[-+]?[0-9]*\.?[0-9]+/);
    if (!match) return 0;
    const n = Number(match[0]);
    return Number.isFinite(n) ? n : 0;
  }

  private collectItemStatEntry(entries: Array<{stat: string; value: number}>, type: string | undefined, rawValue: string | undefined): void {
    if (!type || type.trim().toLowerCase() === 'none') return;
    const value = this.parseItemBonusValue(rawValue);
    if (value === 0) return;
    entries.push({ stat: type.trim(), value });
  }

  private itemDesignStatEntries(design: PssItemDesign): Array<{stat:string; value:number}> {
    const statEntries: Array<{stat:string; value:number}> = [];
    this.collectItemStatEntry(statEntries, design.enhancementType, design.enhancementValue || design.modifier);
    this.collectItemStatEntry(statEntries, design.enhancementType2, design.enhancementValue2 || design.modifier2);
    this.collectItemStatEntry(statEntries, design.bonusEnhancementType, design.bonusEnhancementValue);
    this.collectItemStatEntry(statEntries, design.bonusEnhancementType2, design.bonusEnhancementValue2);
    return statEntries;
  }

  private itemInstanceBonusEntries(item: PssInventoryItem, instanceId: string): Array<{stat:string; value:number}> {
    const statEntries: Array<{stat:string; value:number}> = [];
    const inventoryItem = this.details?.inventory?.find(i => i === item); // later use item itself
    // instance bonuses exist on item instance data, not on design in current model.
    // Use whatever known instance bonuses if available in the local item row.
    if (item.instanceBonuses) {
      for (const inst of item.instanceBonuses.filter(b => b.itemId === instanceId)) {
        this.collectItemStatEntry(statEntries, inst.enhancementType, inst.enhancementValue);
        this.collectItemStatEntry(statEntries, inst.enhancementType2, inst.enhancementValue2);
      }
    }
    return statEntries;
  }

  bonusRows(item: PssInventoryItem): Array<{ label: string; value: string }> {
    const d = this.itemDesigns.get(item.designId);
    if (!d) return [];

    // Fixed design stats stay in their natural order
    const entries: Array<{ stat: string; value: number }> = [];
    this.collectBonusEntries(entries, d.enhancementType, d.enhancementValue || d.modifier);
    this.collectBonusEntries(entries, d.enhancementType2, d.enhancementValue2 || d.modifier2);
    this.collectBonusEntries(entries, d.bonusEnhancementType, d.bonusEnhancementValue);
    this.collectBonusEntries(entries, d.bonusEnhancementType2, d.bonusEnhancementValue2);

    // Per-instance random bonuses: no dedup — each copy's stat must appear individually
    entries.push(...this.sortedInstanceBonuses(item));

    // Always try description fallback for any item, with a priority on hero/special/legendary stats.
    this.collectDescriptionBonusEntries(entries, d.description ?? '');
    return entries.map((entry, idx) => {
      const sign = entry.value > 0 ? '+' : '';
      return {
        label: idx === 0 ? 'Bonus' : `Bonus ${idx + 1}`,
        value: `${this.shortStatLabel(entry.stat)} ${sign}${this.trimZeros(entry.value)}`,
      };
    });
  }

  itemMainStatText(item: PssInventoryItem): string {
    const d = this.itemDesigns.get(item.designId);
    if (!d) return '';
    const entries: Array<{ stat: string; value: number }> = [];
    this.collectBonusEntries(entries, d.enhancementType, d.enhancementValue || d.modifier);
    if (!entries.length) return '';
    const e = entries[0];
    return `${this.shortStatLabel(e.stat)} ${e.value > 0 ? '+' : ''}${this.trimZeros(e.value)}`;
  }

  itemBonusGroups(item: PssInventoryItem): Array<{ stat: string; chips: string[] }> {
    const d = this.itemDesigns.get(item.designId);
    if (!d) return [];

    // Exclude enhancementType (shown separately as main stat) — collect only secondary bonuses
    const entries: Array<{ stat: string; value: number }> = [];
    this.collectBonusEntries(entries, d.enhancementType2, d.enhancementValue2 || d.modifier2);
    this.collectBonusEntries(entries, d.bonusEnhancementType, d.bonusEnhancementValue);
    this.collectBonusEntries(entries, d.bonusEnhancementType2, d.bonusEnhancementValue2);

    // Per-instance random bonuses: no dedup — each copy's stat must appear individually
    entries.push(...this.sortedInstanceBonuses(item));

    const groups = new Map<string, string[]>();
    for (const e of entries) {
      const stat = this.shortStatLabel(e.stat);
      const chip = `${e.value > 0 ? '+' : ''}${this.trimZeros(e.value)}`;
      if (!groups.has(stat)) groups.set(stat, []);
      groups.get(stat)!.push(chip);
    }
    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([stat, chips]) => ({ stat, chips }));
  }

  subtypeLabel(subtype: string): string {
    const m: Record<string, string> = {
      'EquipmentBody':      'Body Armor',
      'EquipmentHead':      'Headgear',
      'EquipmentLeg':       'Leg Gear',
      'EquipmentWeapon':    'Weapon',
      'EquipmentPet':       'Pet',
      'EquipmentAccessory': 'Accessory',
      'EquipmentHand':      'Hand Gear',
      'EquipmentSticker':   'Sticker',
      'Missile':            'Ammo',
    };
    return m[subtype] ?? this.humanizeWords(subtype);
  }

  private isDisplayableItem(item: PssInventoryItem): boolean {
    const itemType = (this.itemDesigns.get(item.designId)?.itemType ?? '').trim().toLowerCase();
    return itemType !== 'none';
  }

  private saveFilters(): void {
    const payload = {
      invSearch: this.invSearch,
      invSubtypeFilter: this.invSubtypeFilter,
      invRarityFilter: this.invRarityFilter,
      invShowNewOnly: this.invShowNewOnly,
      invSortBy: this.invSortBy,
      invSortAsc: this.invSortAsc
    };
    try { localStorage.setItem(InventoryTabComponent.STORAGE_KEY, JSON.stringify(payload)); } catch {}
  }

  private loadFilters(): void {
    try {
      const raw = localStorage.getItem(InventoryTabComponent.STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved.invSearch) this.invSearch = saved.invSearch;
      if (saved.invSubtypeFilter) this.invSubtypeFilter = saved.invSubtypeFilter;
      if (saved.invRarityFilter) this.invRarityFilter = saved.invRarityFilter;
      if (typeof saved.invShowNewOnly === 'boolean') this.invShowNewOnly = saved.invShowNewOnly;
      if (saved.invSortBy) this.invSortBy = saved.invSortBy;
      if (typeof saved.invSortAsc === 'boolean') this.invSortAsc = saved.invSortAsc;
    } catch {}
  }

  private humanizeWords(value: string): string {
    if (!value) return value;
    return value
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private shortStatLabel(stat: string): string {
    const normalized = stat.trim().toLowerCase();
    const map: Record<string, string> = {
      'engine': 'ENG',
      'weapon': 'WPN',
      'science': 'SCI',
      'pilot': 'PIL',
      'attack': 'ATK',
      'ability': 'ABL',
      'repair': 'RPR',
      'hp': 'HP',
      'health': 'HP',
      'fireresistance': 'RST',
      'fire resistance': 'RST',
    };
    return map[normalized] ?? stat.toUpperCase();
  }

  private collectBonusEntries(entries: Array<{ stat: string; value: number }>, rawTypes: string | undefined, rawValues: string | undefined): void {
    const types = this.splitBonusParts(rawTypes);
    const values = this.splitBonusParts(rawValues);
    if (!types.length) return;

    for (let i = 0; i < types.length; i++) {
      const stat = types[i].trim();
      if (!stat || stat.toLowerCase() === 'none') continue;
      const raw = values[i] ?? (values.length === 1 ? values[0] : '');
      const parsed = this.parseBonusValue(raw);
      if (parsed === null || parsed === 0) continue;
      this.pushUniqueBonus(entries, stat, parsed);
    }
  }

  private sortedInstanceBonuses(item: PssInventoryItem): Array<{ stat: string; value: number }> {
    const result: Array<{ stat: string; value: number }> = [];
    if (!item.instanceBonuses) return result;
    for (const inst of item.instanceBonuses) {
      for (const [t, v] of [[inst.enhancementType, inst.enhancementValue], [inst.enhancementType2, inst.enhancementValue2]] as [string | undefined, string | undefined][]) {
        if (!t || t.trim().toLowerCase() === 'none') continue;
        const val = this.parseBonusValue(v ?? '');
        if (val === null || val === 0) continue;
        result.push({ stat: t.trim(), value: val });
      }
    }
    result.sort((a, b) => {
      const cmp = this.shortStatLabel(a.stat).localeCompare(this.shortStatLabel(b.stat));
      return cmp !== 0 ? cmp : a.value - b.value;
    });
    return result;
  }

  private collectDescriptionBonusEntries(entries: Array<{ stat: string; value: number }>, description: string): void {
    if (!description) return;
    const statPattern = '(Weapon|Engine|Science|Pilot|Attack|Ability|Repair|HP|Health)';
    const nextToStat = new RegExp(`${statPattern}[^\\d+-]{0,12}([+-]?\\d+(?:\\.\\d+)?)`, 'ig');
    const beforeStat = new RegExp(`([+-]?\\d+(?:\\.\\d+)?)\\s*${statPattern}`, 'ig');

    let m: RegExpExecArray | null;
    while ((m = nextToStat.exec(description)) !== null) {
      const stat = m[1] ?? '';
      const value = this.parseBonusValue(m[2] ?? '');
      if (!stat || value === null || value === 0) continue;
      this.pushUniqueBonus(entries, stat, value);
    }
    while ((m = beforeStat.exec(description)) !== null) {
      const value = this.parseBonusValue(m[1] ?? '');
      const stat = m[2] ?? '';
      if (!stat || value === null || value === 0) continue;
      this.pushUniqueBonus(entries, stat, value);
    }
  }

  private pushUniqueBonus(entries: Array<{ stat: string; value: number }>, stat: string, value: number): void {
    const normalized = stat.trim().toLowerCase();
    const exists = entries.some(e => e.stat.trim().toLowerCase() === normalized && e.value === value);
    if (exists) return;
    entries.push({ stat, value });
  }

  private splitBonusParts(value: string | undefined): string[] {
    if (!value) return [];
    return value
      .split(/[|;,/]/)
      .map(s => s.trim())
      .filter(Boolean);
  }

  private parseBonusValue(value: string): number | null {
    const cleaned = value.trim();
    if (!cleaned) return null;
    const match = cleaned.match(/[-+]?\d+(?:\.\d+)?/);
    if (!match) return null;
    const n = Number(match[0]);
    return Number.isFinite(n) ? n : null;
  }

  private trimZeros(value: number): string {
    if (Number.isInteger(value)) return String(value);
    return value.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
  }


}
